import { jsx, jsxs, Fragment } from "react/jsx-runtime";
import { useTranslations } from "@vocab/react";
import { Box, Inline, Badge, useResponsiveValue, Text, HiddenVisually, Hidden, TooltipRenderer, IconPositive, ButtonIcon, IconBookmark, Link, Stack, Columns, Column, List } from "braid-design-system";
import { createContext, useContext, memo, forwardRef, useRef } from "react";
import { translations } from "../.vocab/index.mjs";
import { splitView, tappableJob, outlinedJob, trackingPixel, markVisited, showVisited, showHovered } from "./JobCard.css.mjs";
import { JobCardFooter } from "./components/JobCardFooter/JobCardFooter.mjs";
import { MetaLink } from "./components/MetaLink/MetaLink.mjs";
import { LazyLoadedImg } from "./components/LazyLoadedImg/LazyLoadedImg.mjs";
import { translations as translations$1 } from "../components/SaveJobForSignedIn/.vocab/index.mjs";
import { translations as translations$2 } from "../components/SaveJobForSignedOut/.vocab/index.mjs";
import { getJobDataAutomation, generateJobTitleLinkId, highlightFromHTML } from "./utils/helpers.mjs";
const ClickThroughOverlay = ({ children }) => /* @__PURE__ */ jsx(Box, { position: { desktop: "relative" }, display: "inline", children });
const initialState = {};
const JobCardContext = createContext(initialState);
const useJobCardContext = () => {
  const {
    companyLink,
    classificationLinks,
    id,
    isAuthenticated,
    isPremium = false,
    isSelected = false,
    jobMetaLinkOnClick,
    srcLogo,
    saveJob: { isApplied, isSaved, returnUrl, toggleSavedJob },
    tags,
    featureFlags,
    bulletPoints,
    isViewed = false,
    jobDetailPageLink,
    jobDetailPageJobCardOriginLink,
    jobDetailPageCardTitleOriginLink,
    jobLinkOnClick,
    jobTitle: title,
    joraImpressionTrackingUrl,
    listingDate,
    listingDateDescription,
    locationLinks,
    locationMatch,
    listPosition,
    salaryDisplayLabel,
    shouldAddHoverEffect = false,
    shouldAddFocusEffect = false,
    onClick,
    teaser,
    workType,
    workArrangement,
    ...result
  } = useContext(JobCardContext);
  return {
    ...result,
    companyLink,
    classificationLinks,
    id,
    isAuthenticated,
    isPremium,
    isSelected,
    jobMetaLinkOnClick,
    srcLogo,
    saveJob: { isApplied, isSaved, returnUrl, toggleSavedJob },
    tags,
    featureFlags,
    bulletPoints,
    isViewed,
    jobDetailPageLink,
    jobDetailPageJobCardOriginLink,
    jobDetailPageCardTitleOriginLink,
    jobLinkOnClick,
    jobTitle: title,
    joraImpressionTrackingUrl,
    listingDate,
    listingDateDescription,
    locationLinks,
    locationMatch,
    listPosition,
    salaryDisplayLabel,
    shouldAddHoverEffect,
    shouldAddFocusEffect,
    onClick,
    teaser,
    workType,
    workArrangement
  };
};
const JobCardContextProvider = ({
  children,
  ...props
}) => /* @__PURE__ */ jsx(JobCardContext.Provider, { value: { ...props }, children });
const premiumAdBadgeAutomation = "premiumAdBadge";
const JobBadge = ({ label, tone, automation }) => /* @__PURE__ */ jsx(Box, { pointerEvents: "none", children: /* @__PURE__ */ jsx(Badge, { tone, data: { automation }, children: label }) });
const useJobBadges = () => {
  var _a;
  const { t } = useTranslations(translations);
  const {
    tags,
    isPremium,
    featureFlags,
    saveJob: { isApplied }
  } = useJobCardContext();
  const behaviouralCuesEnabled = featureFlags == null ? void 0 : featureFlags.behaviouralCuesEnabled;
  const isForNewJobCardDensityExperiment = featureFlags == null ? void 0 : featureFlags.newJobCardDensity;
  const isEntryLevelBadge = featureFlags == null ? void 0 : featureFlags.entryLevelBadge;
  const newToYouBadgeLabel = behaviouralCuesEnabled && ((_a = tags == null ? void 0 : tags.find(({ type }) => type === "NEW" || type === "UNSEEN")) == null ? void 0 : _a.label);
  const newToYouBadge = newToYouBadgeLabel ? [
    {
      label: newToYouBadgeLabel,
      tone: "positive",
      automation: "newToYouAdBadge"
    }
  ] : [];
  const badges = !isForNewJobCardDensityExperiment && isPremium ? [
    {
      label: t("Featured"),
      tone: "promote",
      automation: premiumAdBadgeAutomation
    }
  ] : newToYouBadge;
  const { urgentBadge, entryLevelBadges, newJobCardDensityBadges } = (tags == null ? void 0 : tags.reduce(
    (allBadges, { type, label }) => {
      switch (type) {
        case "URGENT":
          allBadges.urgentBadge.push({
            label,
            tone: "promote",
            automation: "urgentAdBadge"
          });
          break;
        case "NO_EXPERIENCE":
        case "ENTRY_LEVEL": {
          if (isEntryLevelBadge) {
            allBadges.entryLevelBadges.push({
              label,
              tone: "promote",
              automation: type === "NO_EXPERIENCE" ? "noExperienceAdBadge" : "entryLevelAdBadge"
            });
          }
          break;
        }
        case "EARLY_APPLICANT":
        case "EXPIRES_SOON": {
          if (isForNewJobCardDensityExperiment && !isApplied) {
            allBadges.newJobCardDensityBadges.push({
              label,
              tone: type === "EARLY_APPLICANT" ? "info" : "critical",
              automation: type === "EARLY_APPLICANT" ? "earlyApplicantAdBadge" : "expiresSoonAdBadge"
            });
          }
          break;
        }
      }
      return allBadges;
    },
    {
      urgentBadge: [],
      entryLevelBadges: [],
      newJobCardDensityBadges: []
    }
  )) ?? {
    urgentBadge: [],
    entryLevelBadges: [],
    newJobCardDensityBadges: []
  };
  const sortedBadges = [
    ...urgentBadge,
    // 2. UrgentHiring/Immediate
    ...entryLevelBadges,
    // 3. No Experience/Entry Level
    ...newJobCardDensityBadges
    // future job card density experiment badges
  ];
  return badges.concat(sortedBadges);
};
const JobBadges = ({ badges }) => badges.length ? /* @__PURE__ */ jsx(Inline, { space: "xxsmall", children: badges.map(({ automation, label, tone }, index) => /* @__PURE__ */ jsx(
  JobBadge,
  {
    label,
    tone,
    automation
  },
  index
)) }) : null;
const ClassificationLink = ({
  link,
  type,
  isChild = false
}) => /* @__PURE__ */ jsxs(Fragment, { children: [
  /* @__PURE__ */ jsx(HiddenVisually, { children: `${type}: ${link.name}` }),
  /* @__PURE__ */ jsx(
    MetaLink,
    {
      name: link.name,
      href: link.href,
      rel: link.rel,
      target: link.target,
      title: link.title,
      type,
      isChild,
      disabledLink: true
    }
  )
] });
const JobClassificationsWrapper = ({ children, isForNewJobCardDensityExperiment }) => {
  if (isForNewJobCardDensityExperiment) {
    return children;
  }
  return /* @__PURE__ */ jsx(Hidden, { below: "tablet", children });
};
const JobClassifications = () => {
  const { classificationLinks: link, featureFlags } = useJobCardContext();
  const [classification, subClassification] = link;
  const newJobCardDensity = Boolean(featureFlags == null ? void 0 : featureFlags.newJobCardDensity);
  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  });
  return /* @__PURE__ */ jsx(
    JobClassificationsWrapper,
    {
      isForNewJobCardDensityExperiment: newJobCardDensity,
      children: /* @__PURE__ */ jsx(
        Text,
        {
          size: newJobCardDensity && isMobile ? "xsmall" : "small",
          tone: newJobCardDensity ? "secondary" : void 0,
          children: /* @__PURE__ */ jsxs(Inline, { space: "none", children: [
            subClassification && /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsx(
                ClassificationLink,
                {
                  link: subClassification,
                  type: "subClassification"
                }
              ),
              /* @__PURE__ */ jsx(Box, { marginLeft: "xxsmall" })
            ] }),
            /* @__PURE__ */ jsx(
              ClassificationLink,
              {
                link: classification,
                type: "classification",
                isChild: Boolean(subClassification)
              }
            )
          ] })
        }
      )
    }
  );
};
const SavedJobForApplied = () => {
  const { t } = useTranslations(translations$1);
  const title = t("You have applied for this job");
  const { id } = useJobCardContext();
  const jobId = String(id);
  return /* @__PURE__ */ jsx(
    TooltipRenderer,
    {
      id: `serp_applied_${jobId}`,
      tooltip: /* @__PURE__ */ jsx(Text, { children: title }),
      children: ({ triggerProps }) => /* @__PURE__ */ jsx(
        Box,
        {
          ...triggerProps,
          data: {
            testid: "applied-job-icon",
            automation: "applied-job"
          },
          children: /* @__PURE__ */ jsx(IconPositive, { tone: "positive" })
        }
      )
    }
  );
};
const SaveJobButton = () => {
  const { t } = useTranslations(translations$1);
  const {
    id,
    saveJob: { isSaved, toggleSavedJob }
  } = useJobCardContext();
  const buttonText = isSaved ? t("Unsave") : t("Save");
  const jobId = String(id);
  return /* @__PURE__ */ jsx(
    ButtonIcon,
    {
      id: `serp-save-job-${jobId}`,
      label: buttonText,
      variant: "transparent",
      data: {
        testid: isSaved ? "remove-save-job" : "save-job",
        automation: isSaved ? "remove-saved-job" : "save-job"
      },
      icon: /* @__PURE__ */ jsx(
        IconBookmark,
        {
          tone: isSaved ? "brandAccent" : void 0,
          active: isSaved
        }
      ),
      onClick: (e) => {
        e.stopPropagation();
        toggleSavedJob(e);
      }
    }
  );
};
const SaveJobForSignedIn = () => {
  const {
    saveJob: { isApplied }
  } = useJobCardContext();
  return isApplied ? /* @__PURE__ */ jsx(SavedJobForApplied, {}) : /* @__PURE__ */ jsx(SaveJobButton, {});
};
const SaveJobForSignedOut = () => {
  const { t } = useTranslations(translations$2);
  const {
    id,
    saveJob: { toggleSavedJob }
  } = useJobCardContext();
  const jobId = String(id);
  return /* @__PURE__ */ jsx(
    ButtonIcon,
    {
      id: `serp-signed-out-save-job-${jobId}`,
      label: t("Sign in to save this job"),
      variant: "transparent",
      data: {
        testid: "signed-out-save-job",
        automation: "signed-out-save-job"
      },
      icon: /* @__PURE__ */ jsx(IconBookmark, {}),
      onClick: toggleSavedJob
    }
  );
};
const SaveJob = () => {
  const { isAuthenticated } = useJobCardContext();
  return isAuthenticated ? /* @__PURE__ */ jsx(SaveJobForSignedIn, {}) : /* @__PURE__ */ jsx(SaveJobForSignedOut, {});
};
const SaveJob$1 = memo(SaveJob);
const JobCardBase = forwardRef(
  ({ "aria-label": ariaLabel }, ref) => {
    const {
      companyLink,
      id,
      isPremium,
      isSelected,
      srcLogo,
      tags,
      featureFlags,
      bulletPoints,
      isViewed,
      jobDetailPageLink,
      jobDetailPageJobCardOriginLink,
      jobDetailPageCardTitleOriginLink,
      jobLinkOnClick,
      jobTitle: title,
      joraImpressionTrackingUrl,
      listingDateDescription,
      locationMatch,
      listPosition,
      shouldAddHoverEffect,
      shouldAddFocusEffect,
      onClick,
      teaser
    } = useJobCardContext();
    const { t } = useTranslations(translations);
    const badges = useJobBadges();
    const shouldRenderLogo = srcLogo !== void 0;
    const isForNewJobCardDensityExperiment = featureFlags == null ? void 0 : featureFlags.newJobCardDensity;
    const responsiveValue = useResponsiveValue();
    const shouldRenderBadges = Boolean(badges == null ? void 0 : badges.length) || isPremium;
    const isMobileAndTablet = responsiveValue({
      mobile: true,
      desktop: false
    });
    const isMobile = responsiveValue({
      mobile: true,
      tablet: false
    });
    const showJobCardTeaser = !isForNewJobCardDensityExperiment && !(featureFlags == null ? void 0 : featureFlags.jobCardDensityExperiment1HideTeaser) && ((featureFlags == null ? void 0 : featureFlags.isShowJobCardTeaserInSplitView) || isMobileAndTablet);
    const premiumBadgeDescriptionText = t("This is a featured job");
    const isContainViewedTag = (featureFlags == null ? void 0 : featureFlags.behaviouralCuesEnabled) && (tags == null ? void 0 : tags.some(
      ({ type }) => type === "VIEWED" || type === "APP_STARTED" || type === "APP_FINISHED"
    ));
    return /* @__PURE__ */ jsxs(
      Box,
      {
        ...listPosition && { id: `jobcard-${listPosition}` },
        component: "article",
        cursor: "pointer",
        className: [
          splitView.jobCardBase,
          {
            [splitView.selectedJobCard]: isSelected,
            [splitView.hoveredJobCard]: shouldAddHoverEffect,
            [splitView.focusedJobCard]: shouldAddFocusEffect
          }
        ],
        padding: {
          mobile: isForNewJobCardDensityExperiment ? "medium" : "gutter",
          tablet: "gutter"
        },
        background: "surface",
        borderRadius: "large",
        "aria-label": ariaLabel,
        "aria-selected": isSelected,
        data: {
          automation: getJobDataAutomation(isPremium, locationMatch),
          testid: "job-card",
          "job-id": id,
          "card-type": "JobCard"
        },
        position: "relative",
        onClick,
        ref,
        children: [
          /* @__PURE__ */ jsx(Hidden, { above: "tablet", children: /* @__PURE__ */ jsx(
            Link,
            {
              href: jobDetailPageLink.href || "",
              title: jobDetailPageLink.title,
              rel: jobDetailPageLink.rel,
              className: tappableJob,
              data: {
                automation: "job-list-view-job-link"
              },
              target: (featureFlags == null ? void 0 : featureFlags.shouldOpenApplyInNewTab) ? "_blank" : "_self",
              onClick: jobLinkOnClick
            }
          ) }),
          /* @__PURE__ */ jsx(Hidden, { below: "desktop", children: /* @__PURE__ */ jsx(
            Link,
            {
              href: jobDetailPageJobCardOriginLink.href || "",
              title: jobDetailPageJobCardOriginLink.title,
              rel: jobDetailPageJobCardOriginLink.rel,
              className: [tappableJob, outlinedJob],
              data: {
                automation: "job-list-item-link-overlay",
                testid: "job-list-item-link-overlay",
                "run-click-only": true
              },
              onClick: jobLinkOnClick
            }
          ) }),
          joraImpressionTrackingUrl && /* @__PURE__ */ jsx(
            Box,
            {
              component: "img",
              src: joraImpressionTrackingUrl,
              className: trackingPixel,
              data: {
                testid: "jora-impression-tracking"
              }
            }
          ),
          isPremium ? /* @__PURE__ */ jsx(HiddenVisually, { children: premiumBadgeDescriptionText }) : listingDateDescription && /* @__PURE__ */ jsx(HiddenVisually, { children: listingDateDescription }),
          /* @__PURE__ */ jsxs(Stack, { space: "medium", children: [
            /* @__PURE__ */ jsxs(
              Columns,
              {
                space: "small",
                collapseBelow: isForNewJobCardDensityExperiment ? "tablet" : void 0,
                reverse: isForNewJobCardDensityExperiment,
                children: [
                  isForNewJobCardDensityExperiment && shouldRenderLogo ? /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(
                    LazyLoadedImg,
                    {
                      src: srcLogo,
                      useNewJobCardDensityMobileStyle: Boolean(isMobile)
                    }
                  ) }) : null,
                  /* @__PURE__ */ jsx(Column, { children: /* @__PURE__ */ jsxs(
                    Stack,
                    {
                      space: isForNewJobCardDensityExperiment || shouldRenderLogo ? "xsmall" : "medium",
                      children: [
                        !isForNewJobCardDensityExperiment && (shouldRenderLogo || shouldRenderBadges) ? /* @__PURE__ */ jsxs(Stack, { space: "small", children: [
                          shouldRenderBadges && /* @__PURE__ */ jsx(JobBadges, { badges }),
                          shouldRenderLogo && /* @__PURE__ */ jsx(LazyLoadedImg, { src: srcLogo })
                        ] }) : null,
                        /* @__PURE__ */ jsx(
                          Stack,
                          {
                            space: isForNewJobCardDensityExperiment && badges.length > 0 ? "medium" : "small",
                            children: /* @__PURE__ */ jsxs(Box, { children: [
                              /* @__PURE__ */ jsx(Text, { weight: "strong", component: "h3", children: /* @__PURE__ */ jsx(ClickThroughOverlay, { children: /* @__PURE__ */ jsx(
                                Link,
                                {
                                  href: jobDetailPageCardTitleOriginLink.href || "",
                                  title: jobDetailPageCardTitleOriginLink.title,
                                  rel: jobDetailPageCardTitleOriginLink.rel,
                                  className: {
                                    // markVisited forces the color to be the visited color
                                    [markVisited]: isViewed || isContainViewedTag,
                                    // showVisited styles the :visited pseudo-class
                                    [showVisited]: !isSelected,
                                    [showHovered]: shouldAddHoverEffect
                                  },
                                  data: {
                                    automation: "jobTitle",
                                    testid: "job-card-title",
                                    "run-click-only": true
                                  },
                                  id: generateJobTitleLinkId(id),
                                  onClick: jobLinkOnClick,
                                  children: title
                                }
                              ) }) }),
                              /* @__PURE__ */ jsx(
                                Box,
                                {
                                  paddingTop: isForNewJobCardDensityExperiment ? "xsmall" : "small",
                                  children: /* @__PURE__ */ jsx(Text, { children: /* @__PURE__ */ jsxs(ClickThroughOverlay, { children: [
                                    /* @__PURE__ */ jsxs(HiddenVisually, { children: [
                                      t("at"),
                                      " "
                                    ] }),
                                    /* @__PURE__ */ jsx(
                                      MetaLink,
                                      {
                                        name: companyLink.name,
                                        href: companyLink.href,
                                        rel: companyLink.rel,
                                        target: companyLink.target,
                                        title: companyLink.title,
                                        type: "company"
                                      }
                                    )
                                  ] }) })
                                }
                              ),
                              isForNewJobCardDensityExperiment && badges.length > 0 && /* @__PURE__ */ jsx(Box, { paddingTop: "small", children: /* @__PURE__ */ jsx(JobBadges, { badges }) }),
                              isForNewJobCardDensityExperiment && /* @__PURE__ */ jsx(
                                Box,
                                {
                                  paddingTop: {
                                    mobile: "small",
                                    tablet: "medium"
                                  },
                                  children: /* @__PURE__ */ jsx(JobWorkTypeLocationsSalaryAndClassifications, {})
                                }
                              )
                            ] })
                          }
                        )
                      ]
                    }
                  ) }),
                  !isForNewJobCardDensityExperiment ? /* @__PURE__ */ jsx(Column, { width: "content", children: /* @__PURE__ */ jsx(ClickThroughOverlay, { children: /* @__PURE__ */ jsx(SaveJob$1, {}) }) }) : null
                ]
              }
            ),
            !isForNewJobCardDensityExperiment && /* @__PURE__ */ jsx(JobWorkTypeLocationsSalaryAndClassifications, {}),
            (bulletPoints == null ? void 0 : bulletPoints.length) ? /* @__PURE__ */ jsx(List, { size: "small", space: "xsmall", children: bulletPoints.map((bulletPoint, i) => /* @__PURE__ */ jsx(
              Text,
              {
                size: isForNewJobCardDensityExperiment && isMobile ? "xsmall" : "small",
                tone: isForNewJobCardDensityExperiment ? "secondary" : void 0,
                children: bulletPoint
              },
              i
            )) }) : null,
            showJobCardTeaser ? /* @__PURE__ */ jsx(
              Text,
              {
                size: "small",
                tone: "secondary",
                data: {
                  testid: "job-card-teaser",
                  automation: "jobShortDescription"
                },
                children: highlightFromHTML({ html: teaser, isDescription: true })
              }
            ) : null,
            /* @__PURE__ */ jsx(JobCardFooter, {})
          ] })
        ]
      }
    );
  }
);
const JobWorkTypeLocationsSalaryAndClassifications = () => {
  const {
    locationLinks,
    featureFlags,
    workArrangement,
    salaryDisplayLabel,
    workType,
    classificationLinks
  } = useJobCardContext();
  const { t } = useTranslations(translations);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    workType ? /* @__PURE__ */ jsx(HiddenVisually, { children: /* @__PURE__ */ jsx(Box, { component: "p", children: t("This is a {workType} job", { workType }) }) }) : null,
    /* @__PURE__ */ jsxs(Stack, { space: "xsmall", children: [
      /* @__PURE__ */ jsx(Box, { children: /* @__PURE__ */ jsxs(Text, { size: "small", children: [
        locationLinks == null ? void 0 : locationLinks.map((props, index) => (
          // the locations will be split by comma and space
          // e.g. "Lewisham, Sydney NSW"
          /* @__PURE__ */ jsxs(
            Box,
            {
              component: "span",
              data: {
                automation: "jobCardLocation"
              },
              children: [
                index % 2 !== 0 && ", ",
                /* @__PURE__ */ jsx(
                  MetaLink,
                  {
                    name: props.name,
                    href: props.href,
                    rel: props.rel,
                    target: props.target,
                    title: props.title,
                    type: "location",
                    disabledLink: true
                  }
                )
              ]
            },
            index
          )
        )),
        (featureFlags == null ? void 0 : featureFlags.remoteSearchFilterEnabled) && workArrangement && (locationLinks == null ? void 0 : locationLinks.length) ? /* @__PURE__ */ jsxs(
          Box,
          {
            component: "span",
            marginLeft: "xxsmall",
            data: {
              testid: "work-arrangement"
            },
            children: [
              "(",
              workArrangement,
              ")"
            ]
          }
        ) : null
      ] }) }),
      salaryDisplayLabel ? /* @__PURE__ */ jsx(Box, { "aria-label": `Salary: ${salaryDisplayLabel}`, children: /* @__PURE__ */ jsx(Text, { size: "small", data: { automation: "jobSalary" }, maxLines: 2, children: salaryDisplayLabel }) }) : null,
      !(featureFlags == null ? void 0 : featureFlags.newJobCardDensity) && (classificationLinks == null ? void 0 : classificationLinks.length) ? /* @__PURE__ */ jsx(JobClassifications, {}) : null
    ] })
  ] });
};
const JobCard = forwardRef(
  (props, refOriginal) => {
    const ref = useRef(null);
    return /* @__PURE__ */ jsx(JobCardContextProvider, { ...props, children: /* @__PURE__ */ jsx(JobCardBase, { ...props, ref: refOriginal ?? ref }) });
  }
);
export {
  ClickThroughOverlay,
  JobCard,
  JobClassifications,
  SaveJob$1 as SaveJob,
  useJobCardContext
};
